// ═══════════════════════════════════════════════════════════════
// POLLEN CARD — Second Gift Opportunities
// Path: src/components/dashboard/pollen/PollenRetentionDanger.jsx
//
// "They gave once. Help them give twice."
// Surfaces donors whose only gift ever was made in the
// last 24 months. These are the highest-leverage cultivation
// targets in any fundraising program — most first-time donors
// never make a second gift.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from "react";
import { AlertTriangle, X, Info, Sprout, EyeOff, Eye } from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../../constants/appConfig";
import { supabase } from "../../../lib/supabase";
import {
  getFiscalYearBoundaries,
  summarizeGiftsByFy,
} from "../../../utils/fiscalYear";

const ACCENT_COLOR = "#f59e0b"; // amber

// Donors with any contact log in this window are considered "already reached
// out to" and hidden from the list. Logging the outreach is the dismiss.
const RECENT_CONTACT_WINDOW_DAYS = 30;

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const fmtDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const daysAgo = (dateStr) => {
  const d = new Date(dateStr + "T12:00:00");
  const now = new Date();
  return Math.floor((now - d) / (1000 * 60 * 60 * 24));
};

export const PollenRetentionDanger = ({ onClose }) => {
  const {
    palette,
    navigate,
    dataVersion,
    members,
    cultivationQueue,
    currentUser,
    userProfile,
    allGifts,
    chapterSettings,
  } = useApp();
  const [donors, setDonors] = useState([]);
  const [suppressedCount, setSuppressedCount] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const [sortBy, setSortBy] = useState("size"); // default: largest first — highest opportunity
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [showDismissed, setShowDismissed] = useState(false);

  // Seed dismissed from profile. Matches the LYBNT / SYBNT pattern.
  useEffect(() => {
    const saved = userProfile?.pollen_dismissed_retention_danger;
    if (Array.isArray(saved) && saved.length > 0) {
      setDismissedIds(new Set(saved));
    }
  }, [userProfile]);

  const persistDismissed = async (newSet) => {
    const arr = [...newSet];
    setDismissedIds(newSet);
    const userId = currentUser?.id;
    if (!userId) return;
    try {
      await supabase
        .from("profiles")
        .update({ pollen_dismissed_retention_danger: arr })
        .eq("id", userId);
    } catch (err) {
      console.error("[PollenRetentionDanger] Persist exception:", err);
    }
  };

  const handleDismiss = (memberId, event) => {
    event.stopPropagation();
    const updated = new Set(dismissedIds);
    updated.add(memberId);
    persistDismissed(updated);
  };

  const handleRestore = (memberId, event) => {
    event.stopPropagation();
    const updated = new Set(dismissedIds);
    updated.delete(memberId);
    persistDismissed(updated);
  };

  // ── Candidate derivation from allGifts (single source of truth) ──
  //
  // Rule for Second Gift Opportunities:
  //   lifetime gift count === 1
  //   AND that gift falls in the current fiscal year
  //
  // The "gift in current FY" clause automatically excludes LYBNT and
  // SYBNT donors (by definition they haven't given in the current FY),
  // satisfying the suppression rules without any cross-card lookups.
  const candidates = useMemo(() => {
    if (!Array.isArray(allGifts) || allGifts.length === 0) return [];
    const boundaries = getFiscalYearBoundaries(
      chapterSettings?.fiscalYearStartMonth
    );

    // Group gifts by member once
    const giftsByMember = {};
    allGifts.forEach((g) => {
      const mid = g.member_id;
      if (!mid) return;
      if (!giftsByMember[mid]) giftsByMember[mid] = [];
      giftsByMember[mid].push(g);
    });

    const out = [];
    Object.keys(giftsByMember).forEach((memberId) => {
      const gifts = giftsByMember[memberId];
      if (gifts.length !== 1) return; // Second Gift requires exactly 1 gift
      const gift = gifts[0];
      const { gaveCurrentFy } = summarizeGiftsByFy(
        [gift.gift_date],
        boundaries
      );
      if (!gaveCurrentFy) return; // Must have given in current FY (excludes LYBNT/SYBNT)
      out.push({
        memberId,
        giftDate: gift.gift_date,
        giftAmount: Number(gift.amount) || 0,
        daysSinceGift: daysAgo(gift.gift_date),
      });
    });
    return out;
  }, [allGifts, chapterSettings?.fiscalYearStartMonth]);

  // ── Contact-log suppression (separate from per-user dismissal) ───
  //
  // Still queried independently because allGifts doesn't include the
  // contact_logs table, and we want the 30-day window. Small query —
  // just member_ids of logs in the last 30 days for candidates.
  useEffect(() => {
    if (candidates.length === 0) {
      setDonors([]);
      setSuppressedCount(0);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const contactFloor = new Date();
        contactFloor.setDate(contactFloor.getDate() - RECENT_CONTACT_WINDOW_DAYS);
        const contactFloorStr = contactFloor.toISOString();
        const candidateIds = candidates.map((c) => c.memberId);

        const recentlyContacted = new Set();
        const CHUNK = 200;
        for (let i = 0; i < candidateIds.length; i += CHUNK) {
          const chunk = candidateIds.slice(i, i + CHUNK);
          const { data: logs, error: logErr } = await supabase
            .from("contact_logs")
            .select("member_id")
            .in("member_id", chunk)
            .gte("log_date", contactFloorStr);
          if (logErr || !logs) continue;
          logs.forEach((log) => recentlyContacted.add(log.member_id));
        }

        if (cancelled) return;
        setDonors(
          candidates.filter((d) => !recentlyContacted.has(d.memberId))
        );
        setSuppressedCount(recentlyContacted.size);
      } catch (err) {
        // silent
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [candidates, dataVersion]);

  const sortedDonors = useMemo(() => {
    const list = [...donors];
    if (sortBy === "size") {
      list.sort((a, b) => Number(b.giftAmount || 0) - Number(a.giftAmount || 0));
    } else {
      list.sort((a, b) => b.daysSinceGift - a.daysSinceGift);
    }
    // No hard cap — the scrollable list container below keeps the card
    // from sprawling while letting the user reach every candidate.
    return list;
  }, [donors, sortBy]);

  const visibleDonors = useMemo(
    () =>
      showDismissed
        ? sortedDonors.filter((d) => dismissedIds.has(d.memberId))
        : sortedDonors.filter((d) => !dismissedIds.has(d.memberId)),
    [sortedDonors, dismissedIds, showDismissed]
  );

  const activeDismissedCount = useMemo(
    () => sortedDonors.filter((d) => dismissedIds.has(d.memberId)).length,
    [sortedDonors, dismissedIds]
  );

  const getDonorName = (donor) => {
    const member = members.find((m) => m.id === donor.memberId);
    if (!member) return "—";
    return (
      member.credentialedFullName ||
      `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
      "—"
    );
  };

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "none",
          background: palette.background,
          borderRadius: "10px 10px 0 0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <AlertTriangle size={ICON_SIZES.sm} style={{ color: "#fff" }} />
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 700,
              color: "#fff",
              fontFamily: FONT_FAMILY,
            }}
          >
            Second Gift Opportunities
          </span>
          {donors.length > 0 && (
            <span
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 6,
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontFamily: FONT_FAMILY,
              }}
            >
              {donors.length}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            onClick={() => setShowInfo((prev) => !prev)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
              opacity: showInfo ? 1 : 0.5,
              transition: "opacity 0.15s",
            }}
            title="How this card works"
          >
            <Info size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
          </button>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
          </button>
        </div>
      </div>

      {/* Info box */}
      {showInfo && (
        <div
          style={{
            padding: "10px 16px",
            fontSize: FONT_SIZES.xs,
            lineHeight: 1.5,
            color: palette.text2,
            fontFamily: FONT_FAMILY,
            background: `${ACCENT_COLOR}06`,
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          Industry research shows that <strong>up to 70% of first-time donors
          never give a second gift</strong>. This card surfaces donors whose only
          gift ever was made in the last 24 months. The longer they wait, the
          less likely the second gift becomes. A personal touch now is the
          highest-leverage move in your cultivation playbook.
          <div style={{ marginTop: 8 }}>
            Once you log a contact with a donor, they drop off this list for{" "}
            {RECENT_CONTACT_WINDOW_DAYS} days — logging the outreach is the dismiss.
            {suppressedCount > 0 && (
              <> Currently hiding <strong>{suppressedCount}</strong> donor
              {suppressedCount === 1 ? "" : "s"} you've recently reached.</>
            )}
          </div>
        </div>
      )}

      {/* Subtitle */}
      <div
        style={{
          padding: "8px 16px 2px",
        }}
      >
        <span
          style={{
            fontSize: "0.7rem",
            color: palette.text3,
            fontFamily: FONT_FAMILY,
            fontStyle: "italic",
          }}
        >
          They gave once. Help them give twice — most never do.
        </span>
      </div>

      {/* Controls: sort by + show dismissed */}
      {donors.length > 0 && (
        <div
          style={{
            padding: "6px 16px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                fontSize: "0.65rem",
                fontWeight: 700,
                color: palette.text3,
                letterSpacing: 0.4,
                fontFamily: FONT_FAMILY,
              }}
            >
              SORT
            </span>
            <button
              onClick={() => setSortBy("size")}
              title="Largest first gift first — highest ask potential"
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                padding: "4px 9px",
                borderRadius: 6,
                border: `1px solid ${sortBy === "size" ? ACCENT_COLOR : palette.cardBorder}`,
                background:
                  sortBy === "size" ? `${ACCENT_COLOR}18` : "transparent",
                color: sortBy === "size" ? ACCENT_COLOR : palette.text2,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
              }}
            >
              Largest gift
            </button>
            <button
              onClick={() => setSortBy("date")}
              title="Oldest gift first — time-sensitive"
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                padding: "4px 9px",
                borderRadius: 6,
                border: `1px solid ${sortBy === "date" ? ACCENT_COLOR : palette.cardBorder}`,
                background:
                  sortBy === "date" ? `${ACCENT_COLOR}18` : "transparent",
                color: sortBy === "date" ? ACCENT_COLOR : palette.text2,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
              }}
            >
              Oldest gift
            </button>
          </div>
          {activeDismissedCount > 0 && (
            <button
              onClick={() => setShowDismissed((prev) => !prev)}
              title={
                showDismissed
                  ? "Show only active opportunities"
                  : "Show dismissed donors"
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                padding: "4px 8px",
                borderRadius: 6,
                border: `1px solid ${
                  showDismissed ? ACCENT_COLOR : palette.cardBorder
                }`,
                background: showDismissed
                  ? `${ACCENT_COLOR}18`
                  : "transparent",
                color: showDismissed ? ACCENT_COLOR : palette.text3,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
              }}
            >
              {showDismissed ? (
                <Eye size={ICON_SIZES.xs} />
              ) : (
                <EyeOff size={ICON_SIZES.xs} />
              )}
              {showDismissed ? "Viewing dismissed" : `Dismissed (${activeDismissedCount})`}
            </button>
          )}
        </div>
      )}

      {/* Donor list */}
      {donors.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: palette.text3,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
          }}
        >
          No second-gift opportunities in the last 24 months right now.
        </div>
      ) : visibleDonors.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: palette.text3,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
          }}
        >
          {showDismissed
            ? "No dismissed donors."
            : "You've handled everything on this list. Nice."}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "4px 0",
            maxHeight: 360,
            overflowY: "auto",
          }}
        >
          {visibleDonors.map((donor) => {
            const isDismissed = dismissedIds.has(donor.memberId);
            return (
              <div
                key={donor.memberId}
                onClick={() => {
                  const fullMember = members.find((m) => m.id === donor.memberId);
                  if (fullMember)
                    navigate("memberDetail", fullMember, {
                      from: "retention_danger",
                      suggestedTemplate: "second_gift",
                    });
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 16px",
                  cursor: members.find((m) => m.id === donor.memberId) ? "pointer" : "default",
                  transition: "background 0.1s",
                  opacity: isDismissed ? 0.55 : 1,
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = `${palette.accent1}08`;
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = "transparent";
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: 600,
                      color: palette.text1,
                      fontFamily: FONT_FAMILY,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textDecoration: isDismissed ? "line-through" : "none",
                    }}
                  >
                    {getDonorName(donor)}
                  </div>
                </div>
                {cultivationQueue.some((q) => q.memberId === donor.memberId) && (
                  <Sprout
                    size={ICON_SIZES.xs}
                    style={{ color: "#10b981", flexShrink: 0 }}
                    title="In cultivation queue"
                  />
                )}
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    padding: "2px 5px",
                    borderRadius: 4,
                    background:
                      donor.daysSinceGift > 270
                        ? "#ef444420"
                        : donor.daysSinceGift > 180
                        ? `${ACCENT_COLOR}20`
                        : `${ACCENT_COLOR}10`,
                    color:
                      donor.daysSinceGift > 270 ? "#ef4444" : ACCENT_COLOR,
                    fontFamily: FONT_FAMILY,
                    flexShrink: 0,
                  }}
                >
                  {donor.daysSinceGift}d ago
                </span>
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 700,
                    color: ACCENT_COLOR,
                    fontFamily: FONT_FAMILY,
                    flexShrink: 0,
                  }}
                >
                  {fmtCurrency(donor.giftAmount)}
                </span>
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text3,
                    fontFamily: FONT_FAMILY,
                    flexShrink: 0,
                  }}
                >
                  {fmtDate(donor.giftDate)}
                </span>
                {isDismissed ? (
                  <button
                    onClick={(event) => handleRestore(donor.memberId, event)}
                    title="Restore to the active list"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 2,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      color: palette.text3,
                      flexShrink: 0,
                    }}
                  >
                    <Eye size={ICON_SIZES.xs} />
                  </button>
                ) : (
                  <button
                    onClick={(event) => handleDismiss(donor.memberId, event)}
                    title="Dismiss — this donor already got an appeal from another campaign"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 2,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      color: palette.text3,
                      opacity: 0.55,
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.55)}
                  >
                    <EyeOff size={ICON_SIZES.xs} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
